import { createPortal } from "react-dom";
import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useLabText, GCC, EMIRATES, labField } from "../../lib/labText";
import { QuestionField } from "./QuestionField";

export const ApplyDialog = ({ user, questions, onClose, onDone }) => {
  const { t, lang } = useLabText();
  const [f, setF] = useState({ full_name: user?.name || "", dial: "+971", phone: "", country: "United Arab Emirates", city: "" });
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setCountry = (c) => setF((x) => ({ ...x, country: c, city: "", dial: GCC.find((g) => g.en === c)?.dial || x.dial }));
  const uae = f.country === "United Arab Emirates";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/lab/apply", { full_name: f.full_name, phone: `${f.dial} ${f.phone}`.trim(), country: f.country, city: f.city,
        zone: uae ? EMIRATES.find((x) => x[0] === f.city)?.[2] || "" : "", answers });
      toast.success(t("received"));
      onDone();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Error");
    } finally {
      setBusy(false);
    }
  };
  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center md:p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="lab-apply-dialog" className="bg-[#0B0A08] border border-[#262421] w-full md:max-w-lg max-h-[94vh] overflow-y-auto p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <h3 className="serif text-3xl">{t("req_title")}</h3>
          <button type="button" onClick={onClose} data-testid="lab-apply-close" className="text-[#8A857D] hover:text-[#F2EBE1]"><X size={22} /></button>
        </div>
        <p className="text-xs text-[#8A857D] mt-2">{t("req_note")}</p>
        <div className="grid gap-3 mt-6">
          <input required value={f.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder={t("full_name")} className={labField} data-testid="lab-apply-name" />
          <select value={f.country} onChange={(e) => setCountry(e.target.value)} className={`${labField} bg-[#0B0A08]`} data-testid="lab-apply-country">
            {GCC.map((g) => <option key={g.en} value={g.en}>{lang === "ar" ? g.ar : g.en}</option>)}
          </select>
          {uae ? (
            <select required value={f.city} onChange={(e) => set("city", e.target.value)} className={`${labField} bg-[#0B0A08]`} data-testid="lab-apply-city">
              <option value="">{t("emirate")} · {t("choose")}</option>
              {EMIRATES.map(([en, ar]) => <option key={en} value={en}>{lang === "ar" ? ar : en}</option>)}
            </select>
          ) : (
            <input required value={f.city} onChange={(e) => set("city", e.target.value)} placeholder={t("city")} className={labField} data-testid="lab-apply-city" />
          )}
          <div className="flex gap-2" dir="ltr">
            <select value={f.dial} onChange={(e) => set("dial", e.target.value)} className={`${labField} !w-28 bg-[#0B0A08]`} data-testid="lab-apply-dial">
              {GCC.map((g) => <option key={g.dial} value={g.dial}>{g.dial}</option>)}
            </select>
            <input required type="tel" inputMode="tel" value={f.phone} onChange={(e) => set("phone", e.target.value.replace(/[^\d ]/g, ""))} placeholder={t("phone")} className={labField} data-testid="lab-apply-phone" />
          </div>
        </div>
        <div className="space-y-7 mt-8">
          {questions.map((q, i) => <QuestionField key={q.id} q={q} n={i + 1} value={answers[q.id]} onChange={(v) => setAnswers({ ...answers, [q.id]: v })} />)}
        </div>
        <button type="submit" disabled={busy} data-testid="lab-apply-submit"
          className="w-full mt-8 bg-[#C49A6C] text-[#0B0A08] text-xs uppercase tracking-[0.25em] py-4 hover:bg-[#E7CBA9] transition-colors disabled:opacity-40">{t("send")}</button>
      </form>
    </div>,
    document.body,
  );
};
